import type { StrokeAlign } from "@/document/model/styles";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import type { Point } from "@/document/operations/pathGeometry";

/**
 * Packed triangle mesh: flat xy pairs ready for GL upload / Wasm / workers.
 * P6: DisplayItem and caches store Float32Array end-to-end; no unpack on the critical path.
 */
export type PackedMesh = Float32Array;

export interface MeshKernelBackend {
  strokePolylineToTriangles(
    points: Array<{ x: number; y: number }>,
    strokeWidth: number,
    closed: boolean,
    align?: StrokeAlign,
  ): PackedMesh;
  networkToStrokeTriangles(
    network: VectorNetwork,
    strokeWidth: number,
    align?: StrokeAlign,
  ): PackedMesh;
  networkToStrokeTriangleChunks(
    network: VectorNetwork,
    strokeWidth: number,
    align?: StrokeAlign,
  ): PackedMesh[];
  triangulateEvenOddContours(contours: Point[][]): PackedMesh;
}

/** Flat number[] kernel implementations (current TS tessellation). */
export interface TsMeshKernelImpl {
  strokePolylineToTriangles(
    points: Array<{ x: number; y: number }>,
    strokeWidth: number,
    closed: boolean,
    align?: StrokeAlign,
  ): number[];
  networkToStrokeTriangles(
    network: VectorNetwork,
    strokeWidth: number,
    align?: StrokeAlign,
  ): number[];
  networkToStrokeTriangleChunks(
    network: VectorNetwork,
    strokeWidth: number,
    align?: StrokeAlign,
  ): number[][];
  triangulateEvenOddContours(contours: Point[][]): number[];
}

let tsImpl: TsMeshKernelImpl | null = null;

export function registerTsMeshKernelImpl(impl: TsMeshKernelImpl): void {
  tsImpl = impl;
}

function requireTsImpl(): TsMeshKernelImpl {
  if (!tsImpl) {
    throw new Error("TS mesh kernel impl is not registered (import tessellation bootstrap)");
  }
  return tsImpl;
}

/** Exposed for Wasm hybrid backends that still need TS for unported kernels. */
export function getTsMeshKernelImpl(): TsMeshKernelImpl {
  return requireTsImpl();
}

export function packMesh(vertices: ArrayLike<number>): PackedMesh {
  if (vertices instanceof Float32Array) {
    return vertices;
  }
  const packed = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index++) {
    packed[index] = vertices[index]!;
  }
  return packed;
}

/** Kept for tests / callers that need a plain number[]. Not used on the hot path. */
export function unpackMesh(mesh: PackedMesh): number[] {
  return Array.from(mesh);
}

/** Concatenate Float32Array chunks into a single flat Float32Array. */
export function flattenMeshes(chunks: Float32Array[]): Float32Array {
  if (chunks.length === 0) {
    return new Float32Array(0);
  }
  if (chunks.length === 1) {
    return chunks[0]!;
  }
  let totalLength = 0;
  for (const chunk of chunks) {
    totalLength += chunk.length;
  }
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

const tsPackedBackend: MeshKernelBackend = {
  strokePolylineToTriangles(points, strokeWidth, closed, align = "inside") {
    return packMesh(requireTsImpl().strokePolylineToTriangles(points, strokeWidth, closed, align));
  },
  networkToStrokeTriangles(network, strokeWidth, align = "center") {
    return packMesh(requireTsImpl().networkToStrokeTriangles(network, strokeWidth, align));
  },
  networkToStrokeTriangleChunks(network, strokeWidth, align = "center") {
    return requireTsImpl().networkToStrokeTriangleChunks(network, strokeWidth, align).map(packMesh);
  },
  triangulateEvenOddContours(contours) {
    return packMesh(requireTsImpl().triangulateEvenOddContours(contours));
  },
};

/** Active kernel backend. Swap for Wasm in P1/P2 without changing call sites. */
export let meshKernels: MeshKernelBackend = tsPackedBackend;

export function setMeshKernelBackend(backend: MeshKernelBackend): void {
  meshKernels = backend;
}

export function resetMeshKernelBackendForTests(): void {
  meshKernels = tsPackedBackend;
}

/** P6: wrappers return PackedMesh (Float32Array) directly — no unpackMesh on the hot path. */
export function strokePolylineTriangles(
  points: Array<{ x: number; y: number }>,
  strokeWidth: number,
  closed: boolean,
  align: StrokeAlign = "inside",
): Float32Array {
  return meshKernels.strokePolylineToTriangles(points, strokeWidth, closed, align);
}

export function networkStrokeTriangles(
  network: VectorNetwork,
  strokeWidth: number,
  align: StrokeAlign = "center",
): Float32Array {
  return meshKernels.networkToStrokeTriangles(network, strokeWidth, align);
}

export function networkStrokeTriangleChunks(
  network: VectorNetwork,
  strokeWidth: number,
  align: StrokeAlign = "center",
): Float32Array[] {
  return meshKernels.networkToStrokeTriangleChunks(network, strokeWidth, align);
}

export function evenOddContourTriangles(contours: Point[][]): Float32Array {
  return meshKernels.triangulateEvenOddContours(contours);
}
